import { type FormEvent, useCallback, useEffect, useState } from "react";
import { type Api, ApiError, type KbDocument } from "../api";
import { snippet } from "./Conversations";

type Draft = { title: string; body: string };

const EMPTY_DRAFT: Draft = { title: "", body: "" };

export function BusinessKnowledgeCard({ api }: { api: Api }) {
  const [documents, setDocuments] = useState<KbDocument[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Draft>(EMPTY_DRAFT);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [addDraft, setAddDraft] = useState<Draft>(EMPTY_DRAFT);

  const loadDocuments = useCallback(async () => {
    setError(null);
    try {
      const result = await api.kbDocuments();
      setDocuments(result.documents);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not load your business knowledge. Try again.",
      );
    }
  }, [api]);

  useEffect(() => {
    void loadDocuments();
  }, [loadDocuments]);

  const startEditing = (document: KbDocument) => {
    setDeleteId(null);
    setEditingId(document.id);
    setEditDraft({ title: document.title, body: document.body });
    setError(null);
  };

  const saveEdit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingId || busyId) return;
    const title = editDraft.title.trim();
    const body = editDraft.body.trim();
    if (!title || !body) return;

    setBusyId(editingId);
    setError(null);
    try {
      const result = await api.updateKbDocument(editingId, { title, body });
      setDocuments(
        (current) =>
          current?.map((document) =>
            document.id === editingId ? result.document : document,
          ) ?? current,
      );
      setEditingId(null);
      setEditDraft(EMPTY_DRAFT);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not save that document. Try again.",
      );
    } finally {
      setBusyId(null);
    }
  };

  const toggleDocument = async (document: KbDocument) => {
    if (busyId) return;
    setBusyId(document.id);
    setError(null);
    try {
      const result = await api.updateKbDocument(document.id, {
        enabled: document.enabled === 0,
      });
      setDocuments(
        (current) =>
          current?.map((item) =>
            item.id === document.id ? result.document : item,
          ) ?? current,
      );
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not change that document. Try again.",
      );
    } finally {
      setBusyId(null);
    }
  };

  const deleteDocument = async (id: string) => {
    if (deleteId !== id) {
      setDeleteId(id);
      setEditingId(null);
      setError(null);
      return;
    }
    if (busyId) return;

    setBusyId(id);
    setError(null);
    try {
      await api.deleteKbDocument(id);
      setDocuments(
        (current) =>
          current?.filter((document) => document.id !== id) ?? current,
      );
      setDeleteId(null);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not delete that document. Try again.",
      );
    } finally {
      setBusyId(null);
    }
  };

  const addDocument = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busyId) return;
    const title = addDraft.title.trim();
    const body = addDraft.body.trim();
    if (!title || !body) return;

    setBusyId("new");
    setError(null);
    try {
      const result = await api.createKbDocument(title, body);
      setDocuments((current) => [...(current ?? []), result.document]);
      setAddDraft(EMPTY_DRAFT);
      setAdding(false);
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.status === 409
          ? "You can save up to 20 documents. Delete one before adding another."
          : caught instanceof ApiError
            ? caught.message
            : "Could not add that document. Try again.",
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="card kb-card">
      <div className="card-head">
        <h2>Business knowledge</h2>
        <p className="muted">
          What the chat assistant is allowed to know — opening hours, prices,
          policies.
        </p>
      </div>

      <p className="kb-info">
        Your website&apos;s own content is always included automatically.
      </p>

      {documents === null && !error && <p className="muted">Loading…</p>}

      {documents?.length === 0 && (
        <p className="muted kb-empty">
          Nothing here yet. Paste your opening hours, price list, or FAQ.
        </p>
      )}

      {(documents?.length ?? 0) > 0 && (
        <ul className="kb-list">
          {documents?.map((document) => {
            const isEditing = editingId === document.id;
            const isBusy = busyId === document.id;
            return (
              <li className="kb-item" key={document.id}>
                {isEditing ? (
                  <form className="kb-form" onSubmit={saveEdit}>
                    <label>
                      <span>Title</span>
                      <input
                        maxLength={200}
                        onChange={(event) =>
                          setEditDraft((current) => ({
                            ...current,
                            title: event.target.value,
                          }))
                        }
                        required
                        value={editDraft.title}
                      />
                    </label>
                    <label>
                      <span>What the assistant should know</span>
                      <textarea
                        maxLength={32_768}
                        onChange={(event) =>
                          setEditDraft((current) => ({
                            ...current,
                            body: event.target.value,
                          }))
                        }
                        required
                        rows={5}
                        value={editDraft.body}
                      />
                    </label>
                    <div className="kb-actions">
                      <button
                        className="btn-primary"
                        disabled={
                          isBusy ||
                          !editDraft.title.trim() ||
                          !editDraft.body.trim()
                        }
                        type="submit"
                      >
                        {isBusy ? "Saving…" : "Save"}
                      </button>
                      <button
                        className="btn-secondary"
                        disabled={isBusy}
                        onClick={() => {
                          setEditingId(null);
                          setEditDraft(EMPTY_DRAFT);
                        }}
                        type="button"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="kb-row">
                    <div className="kb-copy">
                      <strong>{document.title}</strong>
                      <p className="muted">{snippet(document.body, 70)}</p>
                    </div>
                    <div className="kb-actions">
                      <label className="kb-toggle">
                        <input
                          aria-label={`Use ${document.title} in chat answers`}
                          checked={document.enabled === 1}
                          disabled={isBusy}
                          onChange={() => void toggleDocument(document)}
                          type="checkbox"
                        />
                        <span>{document.enabled === 1 ? "On" : "Off"}</span>
                      </label>
                      <button
                        className="btn-secondary"
                        disabled={isBusy}
                        onClick={() => startEditing(document)}
                        type="button"
                      >
                        Edit
                      </button>
                      <button
                        className="btn-secondary kb-delete"
                        disabled={isBusy}
                        onClick={() => void deleteDocument(document.id)}
                        type="button"
                      >
                        {isBusy
                          ? "Deleting…"
                          : deleteId === document.id
                            ? "Really delete?"
                            : "Delete"}
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {error && <p className="error">{error}</p>}

      {adding ? (
        <form className="kb-form kb-add-form" onSubmit={addDocument}>
          <label>
            <span>Title</span>
            <input
              maxLength={200}
              onChange={(event) =>
                setAddDraft((current) => ({
                  ...current,
                  title: event.target.value,
                }))
              }
              placeholder="For example, Opening hours"
              required
              value={addDraft.title}
            />
          </label>
          <label>
            <span>What the assistant should know</span>
            <textarea
              maxLength={32_768}
              onChange={(event) =>
                setAddDraft((current) => ({
                  ...current,
                  body: event.target.value,
                }))
              }
              placeholder="Paste the information here."
              required
              rows={5}
              value={addDraft.body}
            />
          </label>
          <div className="kb-actions">
            <button
              className="btn-primary"
              disabled={
                busyId === "new" ||
                !addDraft.title.trim() ||
                !addDraft.body.trim()
              }
              type="submit"
            >
              {busyId === "new" ? "Saving…" : "Save"}
            </button>
            <button
              className="btn-secondary"
              disabled={busyId === "new"}
              onClick={() => {
                setAdding(false);
                setAddDraft(EMPTY_DRAFT);
                setError(null);
              }}
              type="button"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button
          className="btn-secondary kb-add-button"
          onClick={() => {
            setAdding(true);
            setDeleteId(null);
            setError(null);
          }}
          type="button"
        >
          Add document
        </button>
      )}
    </section>
  );
}
